/* jc-experiments — helper API on top of PostHog (loaded by snippets/posthog.liquid,
 * only when PostHog is enabled). Safe to call even before posthog finishes loading;
 * the snippet installs a stub that queues calls.
 *
 * Public API (window.JCExperiments):
 *   .track(event, props)        capture a conversion / custom event
 *   .getVariant(flagKey)        read this visitor's bucket ("control" | "variant" | null)
 *   .onVariant(flagKey, fn)     run fn(variant) once flags resolve
 *   .redirect(flagKey, map)     split-URL test: {variant: "/pages/xyz"} → navigate if bucketed there
 *
 * In-page (tag-based) experiments need NO JS here — snippets/posthog.liquid resolves
 * [data-ph-exp]/[data-ph-variant] automatically. Use this file for conversions and
 * for redirect-style tests.
 */
(function () {
  'use strict';

  function ph() { return window.posthog; }

  function track(event, props) {
    try {
      var p = ph();
      if (p && p.capture) p.capture(event, props || {});
    } catch (e) {}
  }

  function getVariant(key) {
    try {
      var p = ph();
      return p && p.getFeatureFlag ? p.getFeatureFlag(key) : null;
    } catch (e) { return null; }
  }

  function onVariant(key, fn) {
    var p = ph();
    var run = function () { try { fn(getVariant(key)); } catch (e) {} };
    if (p && p.onFeatureFlags) p.onFeatureFlags(run);
    else run();
  }

  /* Split-URL experiment. map = { variantKey: "/target/path", ... }.
     Navigates (replace, so no back-button trap) when the visitor's bucket maps
     to a path other than the current one. getFeatureFlag records the exposure. */
  function redirect(key, map) {
    onVariant(key, function (variant) {
      var url = variant && map[variant];
      if (url && location.pathname !== url.split('?')[0].split('#')[0]) {
        location.replace(url);
      }
    });
  }

  window.JCExperiments = { track: track, getVariant: getVariant, onVariant: onVariant, redirect: redirect };

  /* ── Best-effort conversion auto-capture ──────────────────────────────────
     The bespoke PDP buy sheet fires add_to_cart / initiate_checkout explicitly
     (assets/jc-pdp.js) — that's the authoritative funnel signal. The listeners
     below catch adds from other surfaces (cart drawer, quick-add) that emit the
     theme's Shopify standard cart event, de-duped so a single add is one event. */
  var lastAdd = 0;
  function onCartAdd(source) {
    var now = Date.now();
    if (now - lastAdd < 800) return;
    lastAdd = now;
    track('add_to_cart', { source: source });
  }

  ['cart:add', 'cart:update', 'cart-update', 'cartLinesUpdate'].forEach(function (name) {
    document.addEventListener(name, function (e) { onCartAdd((e && e.type) || name); });
  });
})();

/* ── Engagement instrumentation ───────────────────────────────────────────
   Page-level behavioural events for the A/B test. Every event auto-carries the
   experiment context through PostHog super-props (template_suffix = jc-pdp vs
   jc-pdp-b), so all of them break down by variant with no extra tagging;
   pdp_variant is added explicitly where it's known. All hooks are defensive —
   selectors that don't match on a given page are simply no-ops. */
(function () {
  'use strict';
  function P() { return window.posthog; }
  function cap(name, props) { try { var p = P(); if (p && p.capture) p.capture(name, props || {}); } catch (e) {} }
  function pv() { return window.JC_PDP_CHECKOUT_VARIANT || null; }
  var isPDP = !!document.querySelector('.jc-pdp');
  var now = function () { return (window.performance && performance.now) ? performance.now() : Date.now(); };

  // time_to_first_interaction — how fast they engage (does a variant feel effortless?)
  (function () {
    var fired = false, t0 = now();
    var evs = ['scroll', 'pointerdown', 'keydown', 'touchstart'];
    function first() {
      if (fired) return; fired = true;
      cap('time_to_first_interaction', { ms: Math.round(now() - t0), pdp_variant: pv() });
      evs.forEach(function (e) { window.removeEventListener(e, first, true); });
    }
    evs.forEach(function (e) { window.addEventListener(e, first, { capture: true, passive: true }); });
  })();

  // scroll_depth — 25/50/75/100 %, once each
  (function () {
    var marks = [25, 50, 75, 100], hit = {};
    function onScroll() {
      var doc = document.documentElement, body = document.body;
      var top = window.pageYOffset || doc.scrollTop || 0;
      var h = Math.max(body.scrollHeight, doc.scrollHeight) - window.innerHeight;
      if (h <= 0) return;
      var d = Math.min(100, Math.round((top / h) * 100));
      marks.forEach(function (m) { if (d >= m && !hit[m]) { hit[m] = true; cap('scroll_depth', { depth: m, pdp_variant: pv() }); } });
      if (hit[100]) window.removeEventListener('scroll', onScroll);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  })();

  // engaged_time / time on site — only counts active seconds (tab visible AND
  // interacted within the last 30 s), sent once on the way out.
  (function () {
    var engaged = 0, last = Date.now(), lastAct = Date.now(), sent = false;
    function tick() {
      var t = Date.now();
      if (document.visibilityState === 'visible' && (t - lastAct) < 30000) engaged += (t - last);
      last = t;
    }
    var iv = setInterval(tick, 1000);
    ['pointerdown', 'keydown', 'scroll', 'mousemove', 'touchstart'].forEach(function (e) {
      window.addEventListener(e, function () { lastAct = Date.now(); }, { capture: true, passive: true });
    });
    function flush() {
      if (sent) return; sent = true; tick(); clearInterval(iv);
      cap('engaged_time', { seconds: Math.round(engaged / 1000), pdp_variant: pv() });
    }
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () { tick(); if (document.visibilityState === 'hidden') flush(); });
  })();

  // exit_intent — cursor leaves through the top of the viewport (desktop)
  (function () {
    var fired = false;
    document.addEventListener('mouseout', function (e) {
      if (fired || e.relatedTarget || e.clientY > 0) return;
      fired = true; cap('exit_intent', { pdp_variant: pv() });
    });
  })();

  // Delegated clicks — discount CTAs, reviews, header nav, guarantee
  document.addEventListener('click', function (e) {
    var t = e.target; if (!t || !t.closest) return;
    var a;
    if ((a = t.closest('a[href^="#buy"]'))) cap('discount_cta_click', { label: (a.textContent || '').trim().slice(0, 60), pdp_variant: pv() });
    if ((a = t.closest('a[href="#reviews"], .tp-label'))) cap('reviews_click', { where: 'link', pdp_variant: pv() });
    else if (t.closest('#reviews')) cap('reviews_click', { where: 'widget', pdp_variant: pv() });
    if ((a = t.closest('a[href^="#"]')) && a.closest('header, .jc-header, [class*="header"]')) cap('nav_anchor_click', { target: a.getAttribute('href'), label: (a.textContent || '').trim().slice(0, 40) });
    if (t.closest('[alt*="Garantie"], [class*="guarantee"], .spark-fine')) cap('guarantee_click', { pdp_variant: pv() });
  }, true);

  // faq_open — <details> accordions (both PDP FAQ blocks)
  [].slice.call(document.querySelectorAll('details.faq-i, details.pfaq-i')).forEach(function (d) {
    d.addEventListener('toggle', function () {
      if (!d.open) return;
      var s = d.querySelector('summary');
      cap('faq_open', { question: s ? (s.textContent || '').trim().slice(0, 90) : null, pdp_variant: pv() });
    });
  });

  // section_view — key content sections seen once (did they convert before/after scrolling?)
  if ('IntersectionObserver' in window) {
    var secs = [
      { el: document.querySelector('.kn-ingredients'), name: 'ingredients' },
      { el: document.getElementById('reviews'), name: 'reviews' },
      { el: document.getElementById('fragen'), name: 'faq' }
    ].filter(function (s) { return s.el; });
    if (secs.length) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          for (var i = 0; i < secs.length; i++) {
            if (secs[i].el === en.target && !secs[i].seen) { secs[i].seen = true; cap('section_view', { section: secs[i].name, pdp_variant: pv() }); }
          }
        });
      }, { threshold: 0.4 });
      secs.forEach(function (s) { io.observe(s.el); });
    }
  }

  // video_play — first play per video; `muted` lets you filter out autoplay
  [].slice.call(document.querySelectorAll('video')).forEach(function (v) {
    var seen = false;
    v.addEventListener('play', function () {
      if (seen) return; seen = true;
      cap('video_play', { muted: !!v.muted, autoplay: !!v.autoplay, src: (v.currentSrc || '').split('/').pop(), pdp_variant: pv() });
    });
  });

  // Bridge to off-theme checkout: stash the PostHog distinct_id + session_id as
  // cart attributes so the Shopify Customer-Events pixel can identify() the same
  // person and attribute purchase/checkout events to this visitor's variant.
  if (isPDP) (function () {
    function stash() {
      try {
        var p = P(); if (!p || !p.get_distinct_id) return false;
        if (sessionStorage.getItem('jc_ph_cart_tagged') === '1') return true;
        fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: {
            _ph_did: p.get_distinct_id(),
            _ph_sid: p.get_session_id ? p.get_session_id() : '',
            _ph_variant: window.JC_PDP_CHECKOUT_VARIANT || ''
          } })
        }).then(function () { sessionStorage.setItem('jc_ph_cart_tagged', '1'); }).catch(function () {});
        return true;
      } catch (e) { return false; }
    }
    if (!stash()) { setTimeout(stash, 1500); setTimeout(stash, 4000); }
  })();
})();
